import { type FC, useEffect, useRef, useState } from "react";
import {
  Button,
  Flex,
  ProgressBar,
  Spacing,
  Text,
  useDebug,
  useTheme,
} from "@huub-web/ui";
import { useTranslation } from "react-i18next";
import { Illustration } from "@huub-web/shared";

import { useAuthConfig } from "../../../hooks";
import {
  handleSocialCallback,
  clearAuth0Storage,
  type Auth0Client,
} from "../../../services/auth0";
import { setAuthCookie } from "../../../services/auth-cookie";
import { decodeJwtPayload } from "../../../services/decode-jwt-payload";
import type { BaseSlideProps } from "../../../types";

export type EmailVerificationData = {
  email: string | null;
  auth0Id: string | null;
};

export type CallbackSlideProps = BaseSlideProps & {
  auth0Client: Auth0Client | null;
  onRetry: () => void;
  onSuccess?: () => void;
  onLoadingChange: (loading: boolean) => void;
  onNeedsEmailVerification: (data: EmailVerificationData) => void;
};

/**
 * CallbackSlide component for handling Auth0 redirect callback.
 * Shows processing state while handling the callback, and error state if it fails.
 */
export const CallbackSlide: FC<CallbackSlideProps> = ({
  leading,
  trailing,
  auth0Client,
  onRetry,
  onLoadingChange,
  onSuccess,
  onNeedsEmailVerification,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const { theme } = useTheme();

  const hasIllustration = theme === "light" || theme === "dark";

  /**
   * Tracks whether the callback has been processed (success or error).
   * Once true, prevents any re-processing even if the effect re-runs
   * due to callback reference changes from parent re-renders.
   */
  const hasProcessedRef = useRef(false);

  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;

  const config = useAuthConfig();
  const configRef = useRef(config);
  configRef.current = config;

  const { log } = useDebug();

  useEffect(() => {
    onLoadingChange?.(isProcessing);
  }, [isProcessing, onLoadingChange]);

  useEffect(() => {
    // Only handle code-based callbacks from SPA SDK
    // Password login doesn't use callbacks (direct API call)
    if (!auth0Client) {
      return;
    }

    const handleCallback = async () => {
      // Prevent re-processing if already processed or currently processing.
      // This guard is critical: callback references from parent may change
      // causing this effect to re-run, but we must never process twice.
      if (hasProcessedRef.current) {
        return;
      }
      hasProcessedRef.current = true;

      try {
        log("blue", "AuthCallback", "Using SPA SDK for code exchange");
        const authResult = await handleSocialCallback(
          auth0Client,
          configRef.current.auth0.clientId,
        );

        // Decode the id_token to get auth0Id as fallback (sub claim contains the Auth0 user ID)
        const { sub: auth0IdFromToken, email: emailFromToken } =
          decodeJwtPayload(authResult.idToken);

        // Verify tokens with backend
        const response = await fetch(
          configRef.current.api.verifyTokenEndpoint,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              access_token: authResult.accessToken,
              id_token: authResult.idToken,
              refresh_token: authResult.refreshToken,
            }),
          },
        );

        if (!response.ok) {
          throw new Error(tRef.current("error.verificationFailed"));
        }

        const data = (await response.json()) as {
          email_verified?: boolean;
          email?: string;
          auth0_id?: string;
          id_token?: string | null;
        };

        if (data.email_verified === false) {
          setIsProcessing(false);
          onNeedsEmailVerification({
            email: data.email ?? emailFromToken ?? null,
            auth0Id: data.auth0_id ?? auth0IdFromToken ?? null,
          });
          return;
        }

        // Set the auth cookie for cross-domain sharing with the app
        if (!data.id_token) {
          throw new Error(tRef.current("error.noTokenReceived"));
        }

        setAuthCookie({
          idToken: data.id_token,
          name: configRef.current.cookie.name,
          domain: configRef.current.cookie.domain,
        });
        clearAuth0Storage();
        setIsProcessing(false);
        onSuccess?.();
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : tRef.current("error.processingLogin");

        setError(errorMessage);
        setIsProcessing(false);
      }
    };

    void handleCallback();
  }, [auth0Client, onNeedsEmailVerification, onSuccess, log]);

  return (
    <>
      {isProcessing && <ProgressBar position="absolute" variant="infinite" />}
      {leading}
      <Flex direction="column" gap="m" align="center" justify="center">
        <Flex direction="column" gap="s" align="center">
          {error && (
            <>
              {hasIllustration ? (
                <Illustration name="shrug" />
              ) : (
                <Spacing height="s" />
              )}
              <Flex direction="column" gap="s" align="center">
                <Text as="h2" variant="displayM" bold align="center">
                  {t("callback.errorTitle")}
                </Text>
                <Text as="p" align="center">
                  {t("auth.login.stateFailureMessage")}
                </Text>
              </Flex>
              <Spacing />
              <Button onClick={onRetry}>{t("callback.retryButton")}</Button>
            </>
          )}

          {!error && (
            <>
              {hasIllustration ? (
                <Illustration name="default" />
              ) : (
                <Spacing height="s" />
              )}
              <Text as="h2" variant="displayM" bold align="center">
                {t("callback.processingTitle")}
              </Text>
              <Text as="p" align="center">
                {t("callback.processingMessage")}
              </Text>
            </>
          )}

          <Spacing height="s" />

          {trailing}
        </Flex>
      </Flex>
    </>
  );
};
